import {useCallback, useState} from 'react';

import type {AddButtonProps} from 'shells/card/card-widget/card-widget';
import {Footer} from 'shells/summary/footer';

import {LimitedType, AiFeatureType, LeadFitScore, AiFeaturePeriod} from 'nutshell-graphql-types';
import type {
    WidgetFragment as Widget,
    LeadResearchSummaryFragment as LeadResearchSummary,
} from 'nutshell-graphql-types';

import {AiFeatureGateModal} from '../../../ai-home/ai-feature-gate-modal/ai-feature-gate-modal';
import {useGetAiFeature} from '../../../ai-home/graphql/hooks/use-get-ai-feature';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {useGetLimitsByType} from '../../../limits/use-get-limits-by-type';

import {CompanyOverview} from './components/company-overview';
import {getLeadResearchEmptyStateProps} from './components/helpers';
import {LeadFit} from './components/lead-fit/lead-fit';
import {LeadFitGauge} from './components/lead-fit-gauge/lead-fit-gauge';
import {LeadResearcherSettings} from './components/lead-researcher-settings';
import {ResearchFields} from './components/research-fields';
import {useLeadResearch} from './graphql/hooks/use-lead-research';
import {useLeadResearchPrompt} from './graphql/hooks/use-lead-research-prompt';
import {LeadResearcherSetUpModal} from './lead-researcher-set-up-modal';

import styles from './lead-researcher-widget.module.css';

const MAX_DISPLAYED_FIELDS = 4;

interface Props {
    widget: Widget;
    leadId: string;
    canResearch: boolean;
    existingResearch?: LeadResearchSummary | null;
}

export function LeadResearcherWidget(props: Props) {
    const {user} = useGetSessionUser();
    const isAdmin = user?.permissions.canAccessSetup;
    const {research, isLoading, error, runResearch, rateResearch, isSavingRating} = useLeadResearch(
        props.leadId,
        props.existingResearch
    );
    const {
        selectedFields: savedSelectedFields,
        businessContext: savedBusinessContext,
        idealCustomer: savedIdealCustomer,
        availableFields,
        isSaving,
        updateSettings,
    } = useLeadResearchPrompt();

    const {
        limits: outcomeLimits,
        isLoading: isLoadingLimits,
        refetch: refetchOutcomeLimits,
        isRefetching: isRefetchingOutcomeLimits,
    } = useGetLimitsByType(LimitedType.AI_OUTCOMES);

    const {aiFeature: userAiFeatureUsage, isLoading: isLoadingUserAiFeatureUsage} = useGetAiFeature(
        AiFeatureType.LEAD_RESEARCH,
        AiFeaturePeriod.WEEK,
        false
    );

    const hasUsedLeadResearcherBefore =
        userAiFeatureUsage?.__typename === 'AiFeatureWithStats' &&
        userAiFeatureUsage.loggedInUserHasUsed;

    const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settingsHasChanges, setSettingsHasChanges] = useState(false);
    const isCollapsed = props.widget.isCollapsedForUser;

    // Local settings state (for editing)
    const [localSelectedFields, setLocalSelectedFields] = useState<string[]>([]);
    const [localBusinessContext, setLocalBusinessContext] = useState('');
    const [localIdealCustomer, setLocalIdealCustomer] = useState('');

    const hasConfiguredSettings = Boolean(savedBusinessContext && savedIdealCustomer);

    const handleSettingsClick = useCallback(() => {
        // Initialize local state from saved values when opening settings
        setLocalSelectedFields(savedSelectedFields);
        setLocalBusinessContext(savedBusinessContext || '');
        setLocalIdealCustomer(savedIdealCustomer || '');
        setSettingsHasChanges(false);
        setShowSettings(true);
    }, [savedSelectedFields, savedBusinessContext, savedIdealCustomer]);

    const handleResearch = useCallback(async () => {
        if (outcomeLimits && outcomeLimits.canAdd === false) {
            setShowOutOfCreditsModal(true);
        } else {
            await runResearch();
            refetchOutcomeLimits();
        }
    }, [runResearch, outcomeLimits, refetchOutcomeLimits]);

    const handleSave = useCallback(async () => {
        await updateSettings({
            selectedFields: localSelectedFields,
            businessContext: localBusinessContext || null,
            idealCustomer: localIdealCustomer || null,
        });
        setShowSettings(false);
        setSettingsHasChanges(false);
    }, [updateSettings, localSelectedFields, localBusinessContext, localIdealCustomer]);

    const handleSaveAndResearch = useCallback(async () => {
        if (outcomeLimits && outcomeLimits.canAdd === false) {
            setShowOutOfCreditsModal(true);

            return;
        }

        await handleSave();
        await runResearch();
        refetchOutcomeLimits();
    }, [handleSave, outcomeLimits, runResearch, refetchOutcomeLimits]);

    const isBusinessContextValid = localBusinessContext.trim() !== '';
    const isIdealCustomerValid = localIdealCustomer.trim() !== '';
    const isSettingsFormValid = isBusinessContextValid && isIdealCustomerValid;

    // Determine button props based on current state
    const getAddButtonProps = (): AddButtonProps | undefined => {
        // When we can't research: no button
        if (!props.canResearch) {
            return undefined;
        }

        // When not in settings and no research exists: "Start research"
        if (!research) {
            return {
                variant: 'singular',
                onClick: handleResearch,
                label: isLoading ? 'Researching…' : 'Start research',
                isDisabled: isLoading || isLoadingLimits || isRefetchingOutcomeLimits,
            };
        }

        // Research exists and not in settings: no button
        return undefined;
    };

    // Show mini gauge when collapsed with research
    const secondaryTitle =
        research && isCollapsed && !showSettings ? (
            <div className='px-4 flex align-center' data-tip={`Lead fit: ${research.leadFitLabel}`}>
                <LeadFitGauge score={research.leadFit} width={32} />
            </div>
        ) : undefined;

    const renderSettingsContent = () => (
        <LeadResearcherSettings
            selectedFields={localSelectedFields}
            businessContext={localBusinessContext}
            idealCustomer={localIdealCustomer}
            availableFields={availableFields}
            onSelectedFieldsChange={setLocalSelectedFields}
            onBusinessContextChange={setLocalBusinessContext}
            onIdealCustomerChange={setLocalIdealCustomer}
            onDirtyChange={setSettingsHasChanges}
            originalSelectedFields={savedSelectedFields}
            originalBusinessContext={savedBusinessContext || ''}
            originalIdealCustomer={savedIdealCustomer || ''}
        />
    );

    const renderResearchContent = () => {
        // Limit fields to max 4, filtering out "Unknown" values
        const displayedFields = (research?.fields || [])
            .filter((field) => field.value && field.value.toLowerCase() !== 'unknown')
            .slice(0, MAX_DISPLAYED_FIELDS);

        return (
            <div className={styles['research-content']}>
                <LeadFit
                    score={research?.leadFit || LeadFitScore.UNKNOWN}
                    label={research?.leadFitLabel || 'Unknown'}
                    reasons={research?.leadFitReasons || []}
                    isAdmin={Boolean(isAdmin)}
                    onSettingsClick={handleSettingsClick}
                    hasConfiguredSettings={hasConfiguredSettings}
                />

                {research?.companyOverview ? (
                    <CompanyOverview
                        headline={research.companyOverview.headline}
                        body={research.companyOverview.body}
                    />
                ) : (
                    <CompanyOverview
                        headline={'Company Overview'}
                        body={
                            'Brief summary describing things like a company’s ownership, expertise, services, and more'
                        }
                    />
                )}

                {displayedFields.length > 0 ? (
                    <ResearchFields fields={displayedFields} />
                ) : (
                    <ResearchFields
                        fields={[
                            {key: 'Revenue', label: 'Revenue', value: '$0'},
                            {key: 'Employees', label: 'Employees', value: '0'},
                        ]}
                    />
                )}

                <Footer
                    userRating={research?.userRating || null}
                    onRate={rateResearch}
                    isSavingRating={isSavingRating}
                    onRefresh={
                        research?.canRefresh && !isRefetchingOutcomeLimits
                            ? () => Promise.resolve(handleResearch())
                            : undefined
                    }
                    onSettingsClick={handleSettingsClick}
                    shouldHideSettingsButton={!isAdmin}
                />
            </div>
        );
    };

    const emptyStateProps = getLeadResearchEmptyStateProps(
        research,
        props.canResearch,
        error,
        hasUsedLeadResearcherBefore ?? false
    );

    // Determine what content to render
    const renderContent = () => {
        if (props.canResearch && (!error || research)) {
            return renderResearchContent();
        }

        return null;
    };

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={(isLoading && !showSettings) || isLoadingUserAiFeatureUsage}
                emptyStateProps={emptyStateProps}
                addButtonProps={getAddButtonProps()}
                secondaryTitle={secondaryTitle}
            >
                {renderContent()}
            </CollapsibleCardWidget>
            <LeadResearcherSetUpModal
                handleSave={handleSave}
                handleSaveAndResearch={handleSaveAndResearch}
                isDisabled={isSaving || !settingsHasChanges || !isSettingsFormValid}
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                isOnboarding={!hasConfiguredSettings}
            >
                {renderSettingsContent()}
            </LeadResearcherSetUpModal>
            <AiFeatureGateModal
                isOpen={showOutOfCreditsModal}
                onClose={() => setShowOutOfCreditsModal(false)}
                reason='outOfCredits'
                aiFeatureType={AiFeatureType.LEAD_RESEARCHER}
            />
        </>
    );
}
